/**
 * Lightweight toast notification system.
 *
 * <ToastProvider> at the root, then anywhere call useToast() and
 * `toast.success('Saved')`, `toast.error('Failed')`. Toasts auto-
 * dismiss; stack bottom-right; respect `prefers-reduced-motion`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { classNames } from '@/lib/format';

type ToastVariant = 'success' | 'error' | 'info';

/** Optional inline action (e.g. "Undo"). Clicking it runs onClick and
 *  dismisses the toast. Toasts with an action linger longer so there's
 *  time to react. */
type ToastAction = { label: string; onClick: () => void };

type Toast = {
  id: string;
  message: string;
  variant: ToastVariant;
  action?: ToastAction;
};

type ToastContextValue = {
  push: (message: string, variant?: ToastVariant, action?: ToastAction) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
};

const ToastContext = createContext<ToastContextValue | null>(null);

const DURATION_MS = 3500;
/** Actionable toasts (Undo etc.) stay up longer so there's time to click. */
const ACTION_DURATION_MS = 9000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const counterRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    setToasts((cur) => cur.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (message: string, variant: ToastVariant = 'info', action?: ToastAction) => {
      counterRef.current += 1;
      const id = `t-${counterRef.current}-${Date.now()}`;
      setToasts((cur) => [...cur, { id, message, variant, action }]);
      window.setTimeout(
        () => dismiss(id),
        action ? ACTION_DURATION_MS : DURATION_MS,
      );
    },
    [dismiss],
  );

  const value: ToastContextValue = {
    push,
    success: (m) => push(m, 'success'),
    error: (m) => push(m, 'error'),
    info: (m) => push(m, 'info'),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-live="polite"
        aria-atomic="false"
        className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[100] flex flex-col items-center gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <ToastItem key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastItem({ toast, onDismiss }: { toast: Toast; onDismiss: () => void }) {
  const [entering, setEntering] = useState(true);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setEntering(false));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const tone =
    toast.variant === 'success'
      ? 'border-l-green-700'
      : toast.variant === 'error'
        ? 'border-l-red-700'
        : 'border-l-kg-black';

  return (
    <div
      className={classNames(
        // pu-toast*: PictureUp's raised surface and status colours
        // (pictureup.css; inert in the CRM).
        `pu-toast pu-toast-${toast.variant}`,
        'pointer-events-auto flex items-center gap-3 bg-white dark:bg-kg-grey-800 border-l-4 rounded-xl px-4 py-3 text-sm text-kg-black dark:text-kg-grey-100 shadow-kg-lg min-w-[18rem] max-w-md text-left',
        'transition-all duration-200',
        entering ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0',
        tone,
      )}
    >
      {/* Tapping the message dismisses (the original behaviour). */}
      <button onClick={onDismiss} className="flex-1 text-left">
        {toast.message}
      </button>
      {toast.action && (
        <button
          onClick={() => {
            toast.action!.onClick();
            onDismiss();
          }}
          className="shrink-0 text-xs uppercase tracking-widest font-medium text-kg-black dark:text-white underline underline-offset-2 hover:no-underline"
        >
          {toast.action.label}
        </button>
      )}
    </div>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Don't throw — make calls safe before the provider mounts.
    return {
      push: () => undefined,
      success: () => undefined,
      error: () => undefined,
      info: () => undefined,
    };
  }
  return ctx;
}
